import { IOperatory } from '@dataTypes/appointment.type'
import { default as axios } from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Define the types for the response structure
export type OperatoryResponse = {
  statusCode: number
  message: string
  data:
    | {
        operatories: IOperatory[]
      }
    | object
}

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_OPERATORY')
const headers = {
  'Content-Type': 'application/json',
}

const getRoomOperatoryReferenceValues = (): any => {
  return {
    user_id: JSON.parse(localStorage.getItem('user') || '{}')?.user_id || '',
    clinic_id: localStorage.getItem('clinic_id') || '',
    db_name: localStorage.getItem('db_name') || '',
  }
}

// Function to get all operatories
export const getAllOperatory = async (): Promise<OperatoryResponse> => {
  try {
    const url = `${BUSINESS_LAYER_API}/api/v1/operatory`
    const { data, status } = await axios.get<OperatoryResponse>(url, {
      headers,
    })

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch operatories')
    }
    return data
  } catch (e) {
    logger.error('Error fetching operatories:', e)
    throw toApiError(e, 'Error fetching operatories')
  }
}

export const addNewRoomOperatory = async (room: any): Promise<any> => {
  try {
    const { clinic_id, user_id } = getRoomOperatoryReferenceValues()
    room.clinic_id = clinic_id
    room.user_id = user_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/operatory`,
      room,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in addNewRoomOperatory:', e)
    throw toApiError(e, 'Error adding room operatory')
  }
}

export const updateRoomOperatory = async (
  operatory_id: string,
  room: any,
): Promise<any> => {
  try {
    const { clinic_id, user_id } = getRoomOperatoryReferenceValues()
    room.clinic_id = clinic_id
    room.user_id = user_id
    const payload = {
      operatory_id: operatory_id,
      data: room,
    }
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/operatory`,
      payload,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateRoomOperatory:', e)
    throw toApiError(e, 'Error updating room operatory')
  }
}

export const deleteRoomOperatory = async (
  operatory_id: string,
): Promise<any> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/operatory/${operatory_id}`,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteRoomOperatory:', e)
    throw toApiError(e, 'Error deleting room operatory')
  }
}

export const checkRoomOperatoryIfExists = async (
  roomName: string,
  excludeOperatoryId?: string,
): Promise<any> => {
  try {
    const { clinic_id } = getRoomOperatoryReferenceValues()
    const payload = {
      clinic_id: clinic_id,
      ...(excludeOperatoryId && { exclude_operatory_id: excludeOperatoryId }),
    }

    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/operatory/room-name-exists/${roomName}`,
      payload,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkRoomOperatoryIfExists:', e)
    throw toApiError(e, 'Error checking room operatory')
  }
}

export const updateRoomOperatoriesOrder = async (
  operatories: any[],
): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/operatory/reorder-operatories`,
      { operatories },
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateRoomOperatoriesOrder:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
